import { getLocale } from '@edx/frontend-platform/i18n';
import { useEffect, useRef, useState } from 'react';
import { logError } from '@edx/frontend-platform/logging';

import { courseMetadataApi } from '../api';
import { DEFAULT_LANGUAGE, getCourseCatalogue, LOCALE_MAP } from '../utils';
import {
  CourseData,
  SearchCourseDiscoveryResults,
  CourseCatalogueItem,
  SearchCourseDiscoveryItem,
  AllowedCourseItem,
} from '../types';

export function useCourseCatalogue(allowedCourses: AllowedCourseItem[]) {
  const hasFetchedData = useRef(false);
  const pendingCountRef = useRef(0);
  const unmountedRef = useRef(false);
  const [coursesData, setCoursesData] = useState<CourseCatalogueItem[]>([]);
  const [courseDataLoaded, setCourseDataLoaded] = useState<boolean>(false);
  const [courseMetaDataLoaded, setCourseMetaDataLoaded] = useState<boolean>(false);

  const locale = getLocale();
  const userLanguage = LOCALE_MAP[locale] || DEFAULT_LANGUAGE;

  useEffect(() => {
    unmountedRef.current = false;
    return () => { unmountedRef.current = true; };
  }, []);

  const getCourseMetadataCB = (metadata: CourseData, courseId: string) => {
    setCoursesData((currentData) => (
      currentData.map((item) => (
        item?.search?.data.id === courseId ? { ...item, metadata } : item
      ))
    ));

    pendingCountRef.current -= 1;
    if (pendingCountRef.current === 0 && !unmountedRef.current) {
      setCourseMetaDataLoaded(true);
    }
  };

  const updateAllCoursesData = (searchCourseData: SearchCourseDiscoveryItem[]) => {
    pendingCountRef.current = searchCourseData.length;

    if (pendingCountRef.current === 0) {
      setCourseMetaDataLoaded(true);
      return;
    }

    searchCourseData.forEach((course) => {
      const id = course?.data.id;
      courseMetadataApi.getCourseMeta(id, (metadata: CourseData) => {
        if (!unmountedRef.current) {
          getCourseMetadataCB(metadata, id);
        }
      });
    });
  };

  useEffect(() => {
    if (!hasFetchedData.current && allowedCourses.length > 0) {
      getCourseCatalogue(userLanguage)
        .then((searchCourseData: SearchCourseDiscoveryResults) => {
          const allAllowedCourseIds = new Set(allowedCourses.map(c => c.course_id));

          const filteredSearchCourseData = searchCourseData.results.filter(
            (course) => allAllowedCourseIds.has(course?.data?.id),
          );

          const initialCoursesData: CourseCatalogueItem[] = filteredSearchCourseData.map((course) => ({
            metadata: null,
            search: course,
          }));

          setCoursesData(initialCoursesData);
          setCourseDataLoaded(true);

          updateAllCoursesData(filteredSearchCourseData);

          hasFetchedData.current = true;
        })
        .catch((error) => {
          logError('Error fetching user courses:', error);
        });
    }
  }, [allowedCourses]);

  return [coursesData, courseDataLoaded, courseMetaDataLoaded] as const;
}
